import {FC, memo, useCallback, useMemo} from 'react';
import {Virtuoso} from 'react-virtuoso';
import {Box} from '@chakra-ui/react';
import {useShallow} from 'zustand/react/shallow';
import {api, hasValue, isSameDayEx, IThreadHistoryItem} from '@kcast-fe/core';
import {Loader, useScreenSize} from 'ui-kit';
import {useMessagesStore} from 'stores';

import {ThreadListItem} from './components';

interface IProps {
  query: string;
  onCloseMenu: () => void;
}

const ThreadListFC: FC<IProps> = ({query, onCloseMenu}) => {
  const {data: threads} = api.threads.useThreadsQuery();
  const {data: threadItems = [], isFetching} = api.threads.useThreadHistoryQuery();
  const {filter, actions} = useMessagesStore(useShallow((state) => state));

  const {isLessThan768} = useScreenSize();

  const isThreadSelected = useCallback(
    (thread: IThreadHistoryItem) => {
      if (filter) {
        return filter.id === thread.id && isSameDayEx(thread.timestamp, filter.date);
      } else {
        return false;
      }
    },
    [filter]
  );

  const handleSelect = useCallback(
    (thread: IThreadHistoryItem) => {
      actions.selectThread(thread.id, thread.timestamp);
      if (isLessThan768) {
        onCloseMenu();
      }
    },
    [actions, isLessThan768, onCloseMenu]
  );

  const threadItemsWithNames = useMemo(() => {
    return threadItems.map((threadItem) => ({
      ...threadItem,
      name: threads?.get(threadItem.id) ?? 'Unknown'
    }));
  }, [threadItems, threads]);

  const threadItemsFiltered = useMemo(() => {
    return hasValue(query)
      ? threadItemsWithNames.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()))
      : threadItemsWithNames;
  }, [query, threadItemsWithNames]);

  return (
    <Box p={'0 0 16px 16px'} minH={'1px'} h={'0px'} flex={'1 1 auto'} data-testid={'Threads'}>
      {isFetching ? (
        <Box h={'100%'}>
          <Loader isCenter size={'lg'} />
        </Box>
      ) : (
        <Virtuoso
          data={threadItemsFiltered}
          style={{height: '100%'}}
          skipAnimationFrameInResizeObserver
          computeItemKey={(_, item) => item.timestamp}
          itemContent={(index, item) => {
            const prevItem = threadItemsFiltered[index - 1];
            const showDate = !prevItem || !isSameDayEx(prevItem.timestamp, item.timestamp);
            return (
              <ThreadListItem
                name={item.name}
                timestamp={item.timestamp}
                showDate={showDate}
                isFirst={index === 0}
                isSelected={isThreadSelected(item)}
                onSelect={() => handleSelect(item)}
              />
            );
          }}
        />
      )}
    </Box>
  );
};

export const ThreadList = memo(ThreadListFC);
